import { useApolloClient, useQuery } from '@apollo/client';
import qs from 'qs';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import BaseUrlContext from '../contexts/BaseUrlContext';
import { createOnErrorHandler } from '../components/errors/Errors';
import useEffectOnUpdate from './useEffectOnUpdate';
import { createLogger } from '../utils/logging';
import { getTimeZone } from '../utils';

const logger = createLogger('useExtendedQuery');

function useExtendedQuery(query, options = {}) {
    const {
        variables,
        valueMappingFn,
        withTimeZone,
        variablesToUrl = true,
        skip = false,
    } = options;
    const history = useHistory();

    const baseUrl = useContext(BaseUrlContext);
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();
    const { t, i18n } = useTranslation();

    const getExtendedVariables = useCallback(() => {
        const extendedVariables = { ...variables };
        if (typeof valueMappingFn === 'function') {
            extendedVariables.valueMapping = valueMappingFn(t);
        }
        if (withTimeZone) {
            extendedVariables.timeZone = getTimeZone();
        }
        return extendedVariables;
    }, [variables, valueMappingFn, t, withTimeZone]);

    const {
        loading,
        error,
        data,
        refetch,
        subscribeToMore,
        previousData,
    } = useQuery(query, {
        onError: createOnErrorHandler(dispatch, apolloClient),
        variables: getExtendedVariables(),
        fetchPolicy: 'network-only',
        skip: skip,
    });

    if (error) {
        logger.error(error);
    }
    useEffectOnUpdate(() => {
        if (variablesToUrl) {
            history.push(`${baseUrl}?${qs.stringify(variables)}`);
        }
        refetch(getExtendedVariables());
    }, [refetch, variables, i18n.language, baseUrl, history, getExtendedVariables, variablesToUrl]);

    return {
        loading,
        error,
        data,
        refetch,
        subscribeToMore,
        previousData,
    };
}

export default useExtendedQuery;
